import { isAdmin, isOwner } from "../../services/auth";
import { MyUsageTab } from "./tabs/MyUsageTab";
import { OrgUsageTab } from "./tabs/OrgUsageTab";
import { PlansTab } from "./tabs/PlansTab";
import { Tabs, TabsContent, TabsList, TabsTrigger, Typography } from "@altimateai/lego";
import { useSearchParams } from "react-router-dom";
import { CREDITS_TABS, CreditsTabType } from "./constants";
import { useBreadcrumb } from "@/components/breadcrumbs";

const Credits = () => {
  useBreadcrumb("Credits", {
    defaultParents: [{ label: "Settings", pathname: "/settings", noLink: true }],
  });
  // Role-based access control
  const canAccessOrgUsage = isAdmin() || isOwner();
  const canAccessPlans = isOwner();

  // URL parameter management for tabs
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current tab from URL, default to "my-usage"
  // Auto-switch to plans tab when returning from Stripe checkout
  const hasCheckoutParams =
    searchParams.has("success") ||
    searchParams.has("canceled") ||
    searchParams.has("wallet_deposit");
  const currentTab = hasCheckoutParams
    ? CREDITS_TABS.PLANS
    : searchParams.get("tab") || CREDITS_TABS.MY_USAGE;

  // Validate that the current tab is accessible based on user permissions
  const getValidTab = (requestedTab: string): CreditsTabType => {
    if (requestedTab === CREDITS_TABS.ORG_USAGE && !canAccessOrgUsage) {
      return CREDITS_TABS.MY_USAGE;
    }
    if (requestedTab === CREDITS_TABS.PLANS && !canAccessPlans) {
      return CREDITS_TABS.MY_USAGE;
    }
    // Legacy: redirect old "tokens" tab URL to "plans" (tokens merged into plans)
    if (requestedTab === CREDITS_TABS.TOKENS) {
      return CREDITS_TABS.PLANS;
    }
    return requestedTab as CreditsTabType;
  };

  const validTab = getValidTab(currentTab);

  // Handle tab change
  const handleTabChange = (newTab: string) => {
    setSearchParams({ tab: newTab });
  };

  return (
    <div className="al-max-w-screen-lg">
      <div className="al-mb-6">
        <Typography variant="page-heading">Credits</Typography>
      </div>

      {/* @ts-ignore - Tabs component typing issue with conditional children */}
      <Tabs variant="underlined" value={validTab} onValueChange={handleTabChange}>
        <TabsList className="al-mb-2">
          <TabsTrigger value={CREDITS_TABS.MY_USAGE}>My Usage</TabsTrigger>
          {canAccessOrgUsage && <TabsTrigger value={CREDITS_TABS.ORG_USAGE}>Org Usage</TabsTrigger>}
          {canAccessPlans && <TabsTrigger value={CREDITS_TABS.PLANS}>Plans</TabsTrigger>}
        </TabsList>

        <TabsContent value={CREDITS_TABS.MY_USAGE}>
          <MyUsageTab />
        </TabsContent>

        {canAccessOrgUsage && (
          <TabsContent value={CREDITS_TABS.ORG_USAGE}>
            <OrgUsageTab />
          </TabsContent>
        )}

        {canAccessPlans && (
          <TabsContent value={CREDITS_TABS.PLANS}>
            <PlansTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export { Credits };
